// Pure Lodden Thinks game module — no framework dependencies.
// All functions are pure (no mutation); state is plain data.
//
// Rules: Lodden secretly picks a number. Two Bettors alternate bidding up.
// When a Bettor takes the Under, they bet the number is below the current bid.
// The other Bettor wins if the number is at or above the bid.
// Spectators earn points by predicting the correct side.

import questionDeck from './questionDeck.json';

// --- Config ---

export interface LoddenConfig {
  winningScore: number;  // bettor wins needed to win the game
}

export const DEFAULT_CONFIG: LoddenConfig = {
  winningScore: 5,
};

// --- Types ---

export type Phase = 'setup' | 'question' | 'lock-in' | 'bidding' | 'reveal' | 'round-over' | 'complete';
export type BettorRole = 'bettor-a' | 'bettor-b';
export type PlayerRole = 'lodden' | BettorRole | 'spectator';
export type ActionType =
  | 'confirmSetup' | 'submitQuestion' | 'selectQuestion' | 'drawRandomQuestion'
  | 'lockIn' | 'bid' | 'takeUnder' | 'spectatorPredict'
  | 'acknowledgeReveal' | 'nextRound';

export interface PlayerInit {
  id: string;
  name: string;
  isBot?: boolean;
}

export interface LoddenPlayer {
  id: string;
  name: string;
  role: PlayerRole;
  bettorWins: number;
  spectatorCorrect: number;
  isBot: boolean;
}

export interface Bid {
  bettorId: string;
  bettorRole: BettorRole;
  value: number | null;         // null = take-under action
  action: 'bid' | 'take-under';
}

export interface SpectatorPrediction {
  playerId: string;
  side: 'over' | 'under';
}

export interface QueueItem {
  id: string;
  text: string;
  submittedBy: string;
}

export interface RoundResult {
  line: number;
  loddenNumber: number;
  underWins: boolean;
  winnerId: string;
  winnerRole: BettorRole;
}

export interface LoddenState {
  config: LoddenConfig;
  phase: Phase;
  round: number;
  players: LoddenPlayer[];
  question: string | null;
  questionQueue: QueueItem[];
  loddenNumber: number | null;
  currentBidderRole: BettorRole;
  bids: Bid[];
  spectatorPredictions: SpectatorPrediction[];
  roundResult: RoundResult | null;
  winnerId: string | null;
  rng: number;
}

// View omits the internal rng seed and masks loddenNumber during hidden phases
export interface LoddenView extends Omit<LoddenState, 'rng' | 'loddenNumber'> {
  loddenNumber: number | null;
  viewPlayerId: string | null;
}

export type LoddenAction =
  | { type: 'confirmSetup' }
  | { type: 'submitQuestion'; playerId: string; text: string }
  | { type: 'selectQuestion'; questionId: string }
  | { type: 'drawRandomQuestion' }
  | { type: 'lockIn'; playerId: string; number: number }
  | { type: 'bid'; playerId: string; value: number }
  | { type: 'takeUnder'; playerId: string }
  | { type: 'spectatorPredict'; playerId: string; side: 'over' | 'under' }
  | { type: 'acknowledgeReveal' }
  | { type: 'nextRound' };

// --- Flattened question pool ---

const ALL_QUESTIONS = questionDeck.categories.flatMap(c => c.questions);

// --- Seeded RNG (mulberry32) ---

function rngStep(state: number): [number, number] {
  state = (state + 0x6D2B79F5) | 0;
  let t = Math.imul(state ^ (state >>> 15), 1 | state);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return [state, ((t ^ (t >>> 14)) >>> 0) / 4294967296];
}

// --- Internal helpers ---

function resolveQuestion(text: string, loddenName: string): string {
  return text.replace(/\[Lodden\]/g, loddenName);
}

function assignRoles(players: PlayerInit[], round: number): LoddenPlayer[] {
  const n = players.length;
  return players.map((p, i) => {
    const rotated = ((i - (round - 1)) % n + n) % n;
    const role: PlayerRole =
      rotated === 0 ? 'lodden'
      : rotated === 1 ? 'bettor-a'
      : rotated === 2 ? 'bettor-b'
      : 'spectator';
    return { id: p.id, name: p.name, role, bettorWins: 0, spectatorCorrect: 0, isBot: p.isBot ?? false };
  });
}

function rotatePlayers(players: LoddenPlayer[], round: number): LoddenPlayer[] {
  const n = players.length;
  return players.map((p, i) => {
    const rotated = ((i - (round - 1)) % n + n) % n;
    const role: PlayerRole =
      rotated === 0 ? 'lodden'
      : rotated === 1 ? 'bettor-a'
      : rotated === 2 ? 'bettor-b'
      : 'spectator';
    return { ...p, role };
  });
}

// --- Core API ---

export function createGame(
  config: LoddenConfig,
  players: PlayerInit[],
  seed = Date.now()
): LoddenState {
  return {
    config,
    phase: 'setup',
    round: 1,
    players: assignRoles(players, 1),
    question: null,
    questionQueue: [],
    loddenNumber: null,
    currentBidderRole: 'bettor-a',
    bids: [],
    spectatorPredictions: [],
    roundResult: null,
    winnerId: null,
    rng: seed | 0,
  };
}

export function applyAction(state: LoddenState, action: LoddenAction): LoddenState {
  switch (action.type) {
    case 'confirmSetup':
      return state.phase === 'setup' ? { ...state, phase: 'question' } : state;

    case 'submitQuestion':
      return applySubmitQuestion(state, action.playerId, action.text);

    case 'selectQuestion':
      return applySelectQuestion(state, action.questionId);

    case 'drawRandomQuestion':
      return applyDrawRandom(state);

    case 'lockIn':
      return applyLockIn(state, action.playerId, action.number);

    case 'bid':
      return applyBid(state, action.playerId, action.value);

    case 'takeUnder':
      return applyTakeUnder(state, action.playerId);

    case 'spectatorPredict':
      return applySpectatorPredict(state, action.playerId, action.side);

    case 'acknowledgeReveal':
      if (state.phase !== 'reveal') return state;
      return { ...state, phase: state.winnerId ? 'complete' : 'round-over' };

    case 'nextRound': {
      if (state.phase !== 'round-over') return state;
      const newRound = state.round + 1;
      return {
        ...state,
        phase: 'setup',
        round: newRound,
        players: rotatePlayers(state.players, newRound),
        question: null,
        questionQueue: [],
        loddenNumber: null,
        currentBidderRole: 'bettor-a',
        bids: [],
        spectatorPredictions: [],
        roundResult: null,
      };
    }
  }
}

export function getPlayerView(state: LoddenState, playerId: string | null): LoddenView {
  const viewer = state.players.find(p => p.id === playerId);
  const isLodden = viewer?.role === 'lodden';
  // Hide Lodden's number during lock-in and bidding unless viewer IS Lodden or full/debug view
  const hideLodden = playerId !== null && !isLodden
    && (state.phase === 'lock-in' || state.phase === 'bidding');

  const { rng: _rng, loddenNumber, ...rest } = state;
  return {
    ...rest,
    loddenNumber: hideLodden ? null : loddenNumber,
    viewPlayerId: playerId,
  };
}

export function describe(config: LoddenConfig): string {
  return [
    'Lodden Thinks',
    '',
    'Players rotate roles each round: Lodden, Bettor-A, Bettor-B, and (with 4+ players) Spectators.',
    '',
    'Round sequence:',
    '1. Choose a question (any player submits one, or draw randomly from the deck).',
    '2. Lodden secretly picks any whole number as their answer and locks it in.',
    '3. Bettor-A opens bidding with any number ≥ 1.',
    '   Bettors alternate; each new bid must exceed the previous bid by at least 1.',
    '   The active bettor can "Take the Under" only after at least one bid has been placed.',
    '4. Taking the Under ends bidding.',
    '   Under bettor wins if the true number is BELOW the current bid.',
    '   The other bettor wins if the true number is AT or ABOVE the bid.',
    '5. Spectators earn 1 point by predicting the winning side (over/under) during bidding.',
    '',
    `First bettor to ${config.winningScore} round wins wins the game. Spectator points are tracked separately.`,
  ].join('\n');
}

export function getValidActions(state: LoddenState, playerId: string): ActionType[] {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return [];

  switch (state.phase) {
    case 'setup':
      return ['confirmSetup'];

    case 'question': {
      const actions: ActionType[] = ['submitQuestion', 'drawRandomQuestion'];
      if (state.questionQueue.length > 0) actions.push('selectQuestion');
      return actions;
    }

    case 'lock-in':
      return player.role === 'lodden' ? ['lockIn'] : [];

    case 'bidding': {
      if (player.role === state.currentBidderRole) {
        const hasPriorBid = state.bids.some(b => b.value !== null);
        return hasPriorBid ? ['bid', 'takeUnder'] : ['bid'];
      }
      if (player.role === 'spectator') {
        const hasPredicted = state.spectatorPredictions.some(p => p.playerId === playerId);
        return hasPredicted ? [] : ['spectatorPredict'];
      }
      return [];
    }

    case 'reveal':
      return ['acknowledgeReveal'];

    case 'round-over':
      return ['nextRound'];

    default:
      return [];
  }
}

// Simple bots: deterministic based on player id hash + round.
// Replace with richer logic once the game is stable.
export function getBotAction(player: LoddenPlayer, state: LoddenState): LoddenAction {
  switch (state.phase) {
    case 'setup':
      return { type: 'confirmSetup' };

    case 'question':
      return { type: 'drawRandomQuestion' };

    case 'lock-in': {
      // Deterministic number from player id hash + round
      let seed = state.round * 2654435761;
      for (let i = 0; i < player.id.length; i++) seed ^= player.id.charCodeAt(i) * 31;
      const [, r] = rngStep(seed | 0);
      return { type: 'lockIn', playerId: player.id, number: Math.floor(r * 95) + 3 };
    }

    case 'bidding': {
      if (player.role === 'spectator') {
        const lastBid = [...state.bids].reverse().find(b => b.value !== null);
        const side: 'over' | 'under' = lastBid && lastBid.value! > 50 ? 'under' : 'over';
        return { type: 'spectatorPredict', playerId: player.id, side };
      }
      if (player.role === state.currentBidderRole) {
        const lastBid = [...state.bids].reverse().find(b => b.value !== null);
        const current = lastBid?.value ?? 0;
        if (current >= 70) return { type: 'takeUnder', playerId: player.id };
        return { type: 'bid', playerId: player.id, value: current + 10 };
      }
      return { type: 'confirmSetup' }; // fallback (shouldn't reach here)
    }

    case 'reveal':
      return { type: 'acknowledgeReveal' };

    case 'round-over':
      return { type: 'nextRound' };

    default:
      return { type: 'confirmSetup' };
  }
}

// --- Internal action handlers ---

function applySubmitQuestion(state: LoddenState, playerId: string, text: string): LoddenState {
  if (state.phase !== 'question' || !text.trim()) return state;
  const item: QueueItem = {
    id: `q${state.round}-${state.questionQueue.length}`,
    text: text.trim(),
    submittedBy: playerId,
  };
  return { ...state, questionQueue: [...state.questionQueue, item] };
}

function applySelectQuestion(state: LoddenState, questionId: string): LoddenState {
  if (state.phase !== 'question') return state;
  const item = state.questionQueue.find(q => q.id === questionId);
  if (!item) return state;
  const lodden = state.players.find(p => p.role === 'lodden')!;
  return {
    ...state,
    phase: 'lock-in',
    question: resolveQuestion(item.text, lodden.name),
    questionQueue: [],
  };
}

function applyDrawRandom(state: LoddenState): LoddenState {
  if (state.phase !== 'question') return state;
  const [nextRng, r] = rngStep(state.rng);
  const q = ALL_QUESTIONS[Math.floor(r * ALL_QUESTIONS.length)];
  const lodden = state.players.find(p => p.role === 'lodden')!;
  return {
    ...state,
    phase: 'lock-in',
    question: resolveQuestion(q.text, lodden.name),
    questionQueue: [],
    rng: nextRng,
  };
}

function applyLockIn(state: LoddenState, playerId: string, number: number): LoddenState {
  if (state.phase !== 'lock-in') return state;
  const lodden = state.players.find(p => p.role === 'lodden');
  if (!lodden || lodden.id !== playerId) return state;
  return { ...state, phase: 'bidding', loddenNumber: number };
}

function applyBid(state: LoddenState, playerId: string, value: number): LoddenState {
  if (state.phase !== 'bidding') return state;
  const bidder = state.players.find(p => p.role === state.currentBidderRole);
  if (!bidder || bidder.id !== playerId) return state;

  const lastNumeric = [...state.bids].reverse().find(b => b.value !== null);
  const minValue = lastNumeric ? lastNumeric.value! + 1 : 1;
  if (value < minValue) return state;

  const nextRole: BettorRole = state.currentBidderRole === 'bettor-a' ? 'bettor-b' : 'bettor-a';
  const bid: Bid = { bettorId: playerId, bettorRole: state.currentBidderRole, value, action: 'bid' };
  return { ...state, bids: [...state.bids, bid], currentBidderRole: nextRole };
}

function applyTakeUnder(state: LoddenState, playerId: string): LoddenState {
  if (state.phase !== 'bidding') return state;
  const bidder = state.players.find(p => p.role === state.currentBidderRole);
  if (!bidder || bidder.id !== playerId) return state;

  const lastNumeric = [...state.bids].reverse().find(b => b.value !== null);
  if (!lastNumeric) return state; // no bid to take under of

  const line = lastNumeric.value!;
  const underWins = state.loddenNumber! < line;
  const underRole = state.currentBidderRole;
  const overRole: BettorRole = underRole === 'bettor-a' ? 'bettor-b' : 'bettor-a';
  const winnerRole = underWins ? underRole : overRole;
  const winner = state.players.find(p => p.role === winnerRole)!;

  const players = state.players.map(p => {
    if (p.id === winner.id) return { ...p, bettorWins: p.bettorWins + 1 };
    if (p.role === 'spectator') {
      const pred = state.spectatorPredictions.find(sp => sp.playerId === p.id);
      if (!pred) return p;
      return { ...p, spectatorCorrect: p.spectatorCorrect + ((pred.side === 'under') === underWins ? 1 : 0) };
    }
    return p;
  });

  const takeUnderBid: Bid = { bettorId: playerId, bettorRole: state.currentBidderRole, value: null, action: 'take-under' };
  const roundResult: RoundResult = { line, loddenNumber: state.loddenNumber!, underWins, winnerId: winner.id, winnerRole };
  const gameWinner = players.find(p => p.bettorWins >= state.config.winningScore);

  return {
    ...state,
    phase: 'reveal',
    bids: [...state.bids, takeUnderBid],
    players,
    roundResult,
    winnerId: gameWinner?.id ?? null,
  };
}

function applySpectatorPredict(state: LoddenState, playerId: string, side: 'over' | 'under'): LoddenState {
  if (state.phase !== 'bidding') return state;
  const player = state.players.find(p => p.id === playerId && p.role === 'spectator');
  if (!player) return state;
  if (state.spectatorPredictions.some(p => p.playerId === playerId)) return state;
  return { ...state, spectatorPredictions: [...state.spectatorPredictions, { playerId, side }] };
}
